import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

import { ActivatedRoute } from '@angular/router';

import { PortalApiService } from '../../portal-api.service';
import { PortalAuthService } from '../../portal-auth.service';
import { PortalPreviewService } from '../../portal-preview.service';
import { CurrencyPipe } from '../../shared/currency';
import { portalDate } from '../../shared/portal-format';
import { ToBeConfirmedModalComponent } from '../../shared/to-be-confirmed-modal/to-be-confirmed-modal.component';
import { RejectModalComponent } from '../../shared/reject-modal/reject-modal.component';
import { PortalDocumentOpener } from '../../shared/portal-document';

type COFilter = 'all' | 'pending_review' | 'reviewed' | 'to_be_confirmed' | 'accepted' | 'declined';

@Component({
	selector: 'portal-change-orders',
	templateUrl: './change-orders.component.html'
})
export class ChangeOrdersComponent implements OnInit, OnDestroy {

	// Filter tabs, in display order — matches the quotes section. Counts are keyed to match.
	readonly tabs: { key: COFilter; label: string }[] = [
		{ key: 'all', label: 'All' },
		{ key: 'pending_review', label: 'Pending Review' },
		{ key: 'reviewed', label: 'Reviewed' },
		{ key: 'to_be_confirmed', label: 'To Be Confirmed' },
		{ key: 'accepted', label: 'Accepted' },
		{ key: 'declined', label: 'Declined' }
	];

	@ViewChild('tbcModal') tbcModal!: ToBeConfirmedModalComponent;
	@ViewChild('rejectModal') rejectModal!: RejectModalComponent;

	// Opens documents in new tabs and reloads the list when the customer returns.
	private docs!: PortalDocumentOpener;
	private sub?: Subscription;

	projectId!: string;
	loading = true;
	error = '';

	orders: any[] = [];
	sections: string[] = [];
	counts: any = { all: 0, pending_review: 0, reviewed: 0, to_be_confirmed: 0, accepted: 0, declined: 0 };

	filter: COFilter = 'all';
	search = '';

	constructor(
		private route: ActivatedRoute,
		private api: PortalApiService,
		private auth: PortalAuthService,
		public preview: PortalPreviewService
	) {}

	// Change-order label: the revision name (if any) before the revision number.
	coRevisionLabel(o: any): string {
		return (o?.revision_name ? o.revision_name + ' ' : '') + (o?.revision_label ?? '');
	}

	get filtered(): any[] {
		const q = this.search.trim().toLowerCase();
		return this.orders.filter(o => {
			if (this.filter !== 'all' && o.status !== this.filter) return false;
			if (!q) return true;
			return (`${o.quote_no} ${o.title} ${o.description}`).toLowerCase().includes(q);
		});
	}

	ngOnInit(): void {
		// In a preview the document opens by its own URL (the /preview variant) rather than through
		// the portal's document page, which would have no session in an unframed tab.
		this.docs = new PortalDocumentOpener('', () => this.load(),
			this.preview.ready
				? (quoteId, mode, target, done) => this.api.quote.document(
					quoteId, target, mode,
					(res: any) => done(res?.url || null),
					() => done(null))
				: null);

		// Subscribed rather than read once from the snapshot: Angular reuses this
		// component when only the :id changes, so switching project (from the activity
		// feed, or with the browser's back button) would otherwise leave the previous
		// project's content on screen. Resetting here rather than inside load() keeps
		// the opener's refresh-on-return silent.
		this.sub = this.route.params.subscribe(params => {
			this.projectId = params['id'] || '';
			this.docs.projectId = this.projectId;
			this.loading = true;
			this.error = '';
			this.load();
		});
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
		this.docs?.destroy();
	}

	load(): void {
		this.api.project.changeOrders(this.projectId, (res: any) => {
			this.orders = res?.orders || [];
			this.sections = res?.sections || [];
			this.counts = res?.counts || this.counts;
			this.loading = false;
		}, (err: any) => {
			this.loading = false;
			if (err?.status === 401) { this.auth.goToLogin(); return; }
			this.error = err?.message || 'Could not load change orders.';
		});
	}

	// Signed money for the change value (+/- prefix) — the sign sits outside the
	// currency affix, so it can't go through the pipe on its own.
	signedMoney(value: number, currency?: string): string {
		const v = value || 0;
		return (v > 0 ? '+' : v < 0 ? '-' : '') + CurrencyPipe.transform(Math.abs(v), currency);
	}

	// Every not-yet-decided state — controls whether the accept / reject actions show.
	// Null-safe: the details modal footer can be change-detected before an order is set.
	isPending(o: any): boolean {
		return o?.status === 'pending_review' || o?.status === 'reviewed' || o?.status === 'to_be_confirmed';
	}

	// A change order can be flagged "To be confirmed" while it's still awaiting a
	// decision (Pending Review or Reviewed) — the acknowledgement step before acceptance.
	canConfirm(o: any): boolean {
		return o?.status === 'pending_review' || o?.status === 'reviewed';
	}

	// Open the reason picker; the modal records the note + flag on submit.
	openToBeConfirmed(o: any): void {
		if (!this.canConfirm(o)) return;
		if (this.preview.ready) return;   // the button is disabled; belt-and-braces
		this.tbcModal.open(o);
	}

	// After the modal confirms, move the change order into the To Be Confirmed bucket.
	onToBeConfirmed(e: any): void {
		const o = e?.quote;
		if (!o) return;
		const from = o.status;
		if (this.counts[from] > 0) this.counts[from]--;
		this.counts.to_be_confirmed++;
		o.status = 'to_be_confirmed';
		o.to_be_confirmed = true;
	}

	// Open the reject reason picker; the modal records the note + cancels on submit.
	openReject(o: any): void {
		if (!this.isPending(o)) return;
		if (this.preview.ready) return;   // the button is disabled; belt-and-braces
		this.rejectModal.open(o);
	}

	// After the modal rejects, move the change order into the Declined bucket.
	onRejected(e: any): void {
		const o = e?.quote;
		if (!o) return;
		const from = o.status;
		if (this.counts[from] > 0) this.counts[from]--;
		this.counts.declined++;
		o.status = 'declined';
	}

	// Open the change order's published web proposal full-page in a new tab, via the
	// portal's own session-authenticated document URL (see portal-document.ts).
	openProposal(o: any): void {
		if (o?.can_view_proposal) this.docs.open(o.id);
	}

	// Same, with the sign & accept modal auto-opened on the proposal.
	acceptOrder(o: any): void {
		if (o?.can_accept) this.docs.open(o.id, 'accept');
	}

	// Same, with the add-comment modal auto-opened on the proposal.
	commentOrder(o: any): void {
		if (o?.can_comment) this.docs.open(o.id, 'comment');
	}

	// The change-order web page (line-level detail + accept) is a different document
	// to the proposal, so it is requested explicitly.
	openChangeOrderPage(o: any): void {
		if (o?.can_view_change_order) this.docs.open(o.id, 'view', 'change_order');
	}

	dateLabel(o: any): { text: string; cls: string } {
		if (o.status === 'accepted') {
			return { text: o.accepted_date ? `Accepted on ${portalDate(o.accepted_date)}` : 'Accepted', cls: 'qr-date-accepted' };
		}
		if (o.status === 'declined') {
			return { text: '', cls: 'qr-date-overdue' };
		}
		if (o.expiry_date) {
			const overdue = new Date(o.expiry_date) < new Date();
			return overdue
				? { text: `Overdue ${portalDate(o.expiry_date)}`, cls: 'qr-date-overdue' }
				: { text: `Valid until ${portalDate(o.expiry_date)}`, cls: 'qr-date-pending' };
		}
		return { text: '', cls: 'qr-date-pending' };
	}

	badgeClass(status: string): string {
		switch (status) {
			case 'accepted': return 'badge-success';          // green
			case 'declined': return 'badge-danger';           // purple
			case 'to_be_confirmed': return 'badge-secondary'; // grey
			// Reviewed shares Pending Review's amber badge — no separate colour.
			default: return 'badge-warning';                  // pending_review / reviewed
		}
	}

	badgeLabel(status: string): string {
		switch (status) {
			case 'accepted': return 'Accepted';
			case 'declined': return 'Declined';
			case 'to_be_confirmed': return 'To Be Confirmed';
			case 'reviewed': return 'Reviewed';
			default: return 'Pending Review';
		}
	}
}

